/**
 * Éléments communs aux onglets du module Justificatifs : verrou des contrôles
 * pendant une requête, tableau, retour d'action, liens sûrs, aperçu d'une pièce.
 * Tout est construit par `h()` ; une valeur venue de l'API n'est jamais du HTML.
 */

import { dateFr } from '@scc/core';
import { ErreurApi } from '../../api.js';
import { h, vider } from '../../dom.js';
import { urlFichierPiece, type Piece, type ProvenancePiece, type RubriquePossible } from './api.js';
import { tailleFr } from './pieces-textes.js';

type Controle = HTMLButtonElement | HTMLInputElement | HTMLSelectElement;

/** Désactive les contrôles le temps d'une requête : pas de double envoi. */
export async function verrouille<T>(controles: readonly Controle[], fn: () => Promise<T>): Promise<T> {
  for (const c of controles) c.disabled = true;
  try {
    return await fn();
  } finally {
    for (const c of controles) c.disabled = false;
  }
}

export function table(legende: string, entetes: readonly (string | [string, 'num'])[], lignes: readonly HTMLElement[]): HTMLElement {
  return h(
    'div',
    { classe: 'enveloppe-table' },
    h(
      'table',
      { classe: 'comp-table tableau-arbitrage' },
      h('caption', { classe: 'invisible' }, legende),
      h(
        'thead',
        {},
        h('tr', {}, ...entetes.map((x) => (typeof x === 'string' ? h('th', { scope: 'col' }, x) : h('th', { scope: 'col', classe: 'num' }, x[0])))),
      ),
      h('tbody', {}, ...lignes),
    ),
  );
}

export type NomSection = 'valider' | 'arbitrer' | 'telecharger' | 'historique' | 'regles' | 'sources';

/** Ce que l'onglet met à disposition de ses sections. */
export interface Contexte {
  /** Retour d'action de l'ensemble de l'onglet. */
  readonly retour: Retour;
  /** Relit ces sections auprès du serveur (les cartes déjà là ne sont pas refaites). */
  recharger(...noms: NomSection[]): void;
  /** Rubriques du Drive où la pièce peut être classée (une lecture par exercice). */
  rubriques(p: Piece): Promise<RubriquePossible[]>;
}

/** Texte brut : `h()` rendrait en gras un `**` venu de l'API, un nœud texte non. */
export const brut = (texte: string): Text => document.createTextNode(texte);

/** Message d'une erreur d'API, ou un texte par défaut. */
export const messageDe = (e: unknown, defaut = 'Opération refusée.'): string => (e instanceof ErreurApi ? e.message : defaut);

/** Zone de retour d'une action (succès en vert, refus en rouge), annoncée aux lecteurs d'écran. */
export interface Retour {
  readonly el: HTMLElement;
  ok(...contenu: (Node | string)[]): void;
  ko(texte: string): void;
  effacer(): void;
}

export function creerRetour(): Retour {
  const el = h('div', { role: 'status' });
  return {
    el,
    ok(...contenu) {
      vider(el);
      el.appendChild(h('p', { classe: 'retour-editeur ok' }, ...contenu.map((c) => (typeof c === 'string' ? brut(c) : c))));
    },
    ko(texte) {
      vider(el);
      el.appendChild(h('p', { classe: 'retour-editeur ko', role: 'alert' }, brut(texte)));
    },
    effacer() {
      vider(el);
    },
  };
}

/** Lien vers une page externe, seulement si l'adresse est en http(s) ; sinon le libellé seul. */
export function lienSur(href: string, libelle: string, aria?: string): Node {
  let ok = false;
  try {
    const u = new URL(href);
    ok = u.protocol === 'https:' || u.protocol === 'http:';
  } catch {
    ok = false;
  }
  return ok
    ? h('a', { href, target: '_blank', rel: 'noopener noreferrer', 'aria-label': aria ?? null }, libelle)
    : brut(libelle);
}

const IMAGES_AFFICHABLES: ReadonlySet<string> = new Set(['image/jpeg', 'image/png']);

/**
 * Aperçu du fichier d'une pièce. Une image (JPEG, PNG) s'affiche en place ;
 * un PDF s'ouvre dans un nouvel onglet (la politique de sécurité de l'API
 * interdit de l'encadrer ici) ; un HEIC se télécharge seulement.
 */
export function apercuPiece(p: Piece): HTMLElement {
  if (!p.fichier) {
    return h('div', { classe: 'apercu-piece vide' }, 'Le fichier n’est plus conservé sur le serveur.');
  }
  const url = urlFichierPiece(p.id);
  const lien = (libelle: string): HTMLElement =>
    h('a', { classe: 'btn-action', href: url, target: '_blank', rel: 'noopener noreferrer' }, libelle);
  const infos = h('div', { classe: 'note-lecture' }, brut(`${p.nomOrigine} · ${tailleFr(p.taille)}`));
  if (IMAGES_AFFICHABLES.has(p.typeMime)) {
    return h(
      'div',
      { classe: 'apercu-piece' },
      h('a', { href: url, target: '_blank', rel: 'noopener noreferrer', title: 'Ouvrir l’image en grand' }, h('img', { src: url, alt: `Aperçu de ${p.nomOrigine}`, loading: 'lazy' })),
      infos,
    );
  }
  return h(
    'div',
    { classe: 'apercu-piece' },
    lien(p.typeMime === 'application/pdf' ? 'Ouvrir le PDF ↗' : 'Ouvrir le fichier ↗'),
    infos,
    p.typeMime === 'application/pdf' ? null : h('div', { classe: 'note-lecture' }, 'Format HEIC : le navigateur le télécharge, il ne l’affiche pas.'),
  );
}

/** D'où vient la pièce : expéditeur, boîte, date, sujet et lien vers le message. */
export function resumeProvenance(prov: ProvenancePiece | null, autres = 0): HTMLElement {
  if (prov === null) return h('div', { classe: 'note-lecture' }, 'Origine inconnue (fichier déposé à la main).');
  return h(
    'div',
    { classe: 'provenance-piece' },
    h('div', {}, h('strong', {}, brut(prov.expediteur || 'Expéditeur inconnu')), brut(` · ${prov.boite} · ${dateFr(prov.date.slice(0, 10))}`)),
    prov.sujet === '' ? null : h('div', { classe: 'note-lecture' }, brut(`« ${prov.sujet} »`)),
    prov.lien === '' ? null : h('div', { classe: 'note-lecture' }, lienSur(prov.lien, 'Ouvrir le message ↗', 'Ouvrir le message d’origine dans un nouvel onglet')),
    autres > 0 ? h('div', { classe: 'note-lecture' }, `Reçue aussi par ${String(autres)} autre${autres > 1 ? 's' : ''} message${autres > 1 ? 's' : ''}.`) : null,
  );
}

/** Une section : son en-tête numéroté, un sous-titre qui annonce le nombre de pièces, son corps. */
export interface Section {
  readonly el: HTMLElement;
  readonly corps: HTMLElement;
  /** Sous-titre : le texte fixe, précédé du nombre quand on le donne. */
  sousTitre(n: number | null): void;
}

export function creerSection(numero: number, titre: string, sousTitre: string, ...avant: (Node | null)[]): Section {
  const sub = h('p', { classe: 'section-sub' }, sousTitre);
  const corps = h('div', {});
  const el = h(
    'section',
    { id: `pieces-section-${String(numero)}` },
    h('div', { classe: 'section-head' }, h('div', { classe: 'section-number', 'aria-hidden': 'true' }, String(numero)), h('div', {}, h('h2', {}, titre), sub)),
    ...avant,
    corps,
  );
  return {
    el,
    corps,
    sousTitre(n) {
      sub.textContent = n === null || n === 0 ? sousTitre : `${String(n)} pièce${n > 1 ? 's' : ''} · ${sousTitre}`;
    },
  };
}
